import { Link } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useUpcomingBirthdays } from '@/hooks/use-api'
import { Cake } from 'lucide-react'
import { CATEGORICAL, GRACKLE_COLORS } from '@/lib/theme'

function getInitials(first?: string | null, last?: string | null): string {
  return `${(first ?? '').charAt(0)}${(last ?? '').charAt(0)}`.toUpperCase() || '?'
}

function ordinal(d: number): string {
  const s = ['th', 'st', 'nd', 'rd']
  const v = d % 100
  const suffix = s[(v - 20) % 10] ?? s[v] ?? s[0] ?? 'th'
  return `${d}${suffix}`
}

export function BirthdaysWidget() {
  const { data, isLoading, error } = useUpcomingBirthdays()
  const items = data ?? []

  if (error) return null
  if (!isLoading && items.length === 0) return null

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Cake className="h-4 w-4" style={{ color: CATEGORICAL.plum.solid }} aria-hidden="true" />
          Birthdays This Month
        </CardTitle>
        <CardDescription>
          {items.length} {items.length === 1 ? 'person' : 'people'} celebrating
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p role="status" className="text-sm text-muted-foreground">
            Loading…
          </p>
        ) : (
          <ul className="space-y-2" aria-label="Upcoming birthdays">
            {items.map((b) => (
              <li key={b.id}>
                <Link
                  to={`/hr/employees/${b.id}`}
                  className="flex items-center gap-3 p-2 rounded hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  {b.profile_photo_url ? (
                    <img
                      src={b.profile_photo_url}
                      alt=""
                      className="h-8 w-8 rounded-full object-cover bg-muted"
                    />
                  ) : (
                    <div
                      className="h-8 w-8 rounded-full flex items-center justify-center text-xs font-semibold"
                      style={{
                        backgroundColor: CATEGORICAL.plum.tint,
                        color: GRACKLE_COLORS.prussianBlue,
                      }}
                      aria-hidden="true"
                    >
                      {getInitials(b.first_name, b.last_name)}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {b.preferred_name || b.first_name} {b.last_name}
                    </p>
                    <p className="text-xs text-muted-foreground">{ordinal(b.birth_day)}</p>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}
